import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Autocomplete,
  Drawer,
  FormControl,
  InputLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Divider,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Delete,
  Search,
  FilterList,
  Schedule,
  Add as AddIcon,
  Delete as DeleteIcon,
  Business,
  Description,
  Receipt,
  Assessment,
  AutoAwesome,
  SwapHoriz,
  Settings,
  Warehouse,
  Report,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService, Supplier } from '../../services/apiService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import SettingsSidebar from '../../components/Common/SettingsSidebar';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

type CurrentView = 
  | 'warehouse_stock'
  | 'orders_listing'
  | 'supplier_inquiry' 
  | 'supplier_expenses' 
  | 'supplier_sale_report' 
  | 'returns' 
  | 'process' 
  | 'reports';

const SupplierDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [tabValue, setTabValue] = useState(0);
  const [currentView, setCurrentView] = useState<CurrentView>('warehouse_stock');
  const [loading, setLoading] = useState(false);
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [supplierTypes, setSupplierTypes] = useState<any[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<any[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [queries, setQueries] = useState<any[]>([]);
  const [stockOutItems, setStockOutItems] = useState<any[]>([]);
  const [cancelledOrders, setCancelledOrders] = useState<any[]>([]);
  const [pendingOrders, setPendingOrders] = useState<any[]>([]);
  const [warehouseStock, setWarehouseStock] = useState<any[]>([]);
  const [supplierOrders, setSupplierOrders] = useState<any[]>([]);
  const [supplierOrdersList, setSupplierOrdersList] = useState<any[]>([]);

  useEffect(() => {
    if (id) {
      fetchSupplier();
      fetchSupplierQueries();
      fetchStockOutItems();
      fetchCancelledOrders();
      fetchPendingOrders();
      fetchWarehouseStock();
      fetchSupplierOrdersList();
    }
  }, [id]);

  const fetchSupplier = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const response = await apiService.getSupplier(parseInt(id));
      setSupplier(response.data);
    } catch (error: any) {
      console.error('Failed to fetch supplier:', error);
      toast.error('Failed to load supplier data');
      navigate('/suppliers');
    } finally {
      setLoading(false);
    }
  };

  const fetchSupplierQueries = async () => {
    if (!id) return;
    
    try {
      const response = await apiService.getCallRecords();
      const allQueries = Array.isArray(response.data) ? response.data : [];
      
      console.log('=== SUPPLIER QUERIES DEBUG ===');
      console.log('Supplier ID:', id);
      console.log('All Queries:', allQueries);
      
      // Only show queries related to this specific supplier
      const supplierQueries = allQueries.filter((query: any) => 
        query.supplier_id && query.supplier_id.toString() === id
      );
      
      // Only show supplier-specific queries, not all queries
      const queriesToShow = supplierQueries;
      
      console.log('Filtered Supplier Queries:', supplierQueries);
      console.log('Queries to Show:', queriesToShow);
      setQueries(queriesToShow);
    } catch (error: any) {
      console.error('Failed to fetch supplier queries:', error);
    }
  };

  const fetchStockOutItems = async () => {
    if (!id) return;
    
    try {
      // Fetch both stocks and orders
      const [stocksResponse, ordersResponse] = await Promise.all([
        apiService.getStocks(),
        apiService.getOrders()
      ]);
      
      const allStocks = Array.isArray(stocksResponse.data) ? stocksResponse.data : [];
      const allOrders = Array.isArray(ordersResponse.data) ? ordersResponse.data : [];
      
      console.log('=== SUPPLIER EXPENSES DEBUG ===');
      console.log('Supplier ID:', id);
      console.log('All Stocks:', allStocks);
      console.log('All Orders:', allOrders);
      
      // Filter stock out transactions for this supplier
      const supplierStockOut = allStocks.filter((stock: any) => 
        stock.supplier_id && stock.supplier_id.toString() === id && 
        stock.transaction_type === 'out'
      );
      
      console.log('Supplier Stock Out:', supplierStockOut);
      
      // Get orders that match the reference_number from stock out transactions
      const orderReferenceNumbers = supplierStockOut
        .map((stock: any) => stock.reference_number)
        .filter((ref: string) => ref && ref.trim() !== '');
      
      console.log('Order Reference Numbers:', orderReferenceNumbers);
      
      const supplierOrders = allOrders.filter((order: any) => 
        orderReferenceNumbers.includes(order.teckit_no || order.order_number)
      );
      
      console.log('Filtered Supplier Orders:', supplierOrders);
      
      setStockOutItems(supplierOrders);
    } catch (error: any) {
      console.error('Failed to fetch stock out items:', error);
    }
  };

  const fetchCancelledOrders = async () => {
    if (!id) return;
    
    try {
      const response = await apiService.getOrders();
      const allOrders = Array.isArray(response.data) ? response.data : [];
      // Filter cancelled/deleted orders for this supplier
      const supplierCancelledOrders = allOrders.filter((order: any) => 
        order.supplier_id && order.supplier_id.toString() === id && 
        (order.status === 'cancelled' || order.status === 'deleted')
      );
      setCancelledOrders(supplierCancelledOrders);
    } catch (error: any) {
      console.error('Failed to fetch cancelled orders:', error);
    }
  };

  const fetchPendingOrders = async () => {
    if (!id) return;
    
    try {
      const response = await apiService.getOrders();
      const allOrders = Array.isArray(response.data) ? response.data : [];
      // Filter pending orders for this supplier
      const supplierPendingOrders = allOrders.filter((order: any) => 
        order.supplier_id && order.supplier_id.toString() === id && 
        order.status === 'pending'
      );
      setPendingOrders(supplierPendingOrders);
    } catch (error: any) {
      console.error('Failed to fetch pending orders:', error);
    }
  };

  const fetchWarehouseStock = async () => {
    if (!id) {
      console.log('No supplier ID provided');
      return;
    }
    
    try {
      console.log('=== STARTING WAREHOUSE STOCK FETCH ===');
      console.log('Supplier ID:', id);
      
      // First try to get stocks directly by supplier ID using the new endpoint
      const stocksResponse = await apiService.getStocksBySupplier(parseInt(id));
      console.log('Raw API Response:', stocksResponse);
      
      const supplierStocks = Array.isArray(stocksResponse.data) ? stocksResponse.data : [];
      console.log('Supplier Stocks Count:', supplierStocks.length);
      console.log('Supplier Stocks:', supplierStocks);
      
      // If no stocks found with supplier_id, try to get all stocks and filter by supplier ID
      let stocksToProcess = supplierStocks;
      if (supplierStocks.length === 0) {
        console.log('No stocks found with supplier_id, trying fallback method...');
        const allStocksResponse = await apiService.getStocks();
        const allStocks = Array.isArray(allStocksResponse.data) ? allStocksResponse.data : [];
        
        // Filter by supplier_id directly
        stocksToProcess = allStocks.filter((stock: any) => 
          stock.supplier_id && stock.supplier_id.toString() === id
        );
        console.log('Fallback - Stocks filtered by supplier_id:', stocksToProcess);
        
        // If still no stocks found, show empty state
        if (stocksToProcess.length === 0) {
          console.log('No stocks found for this supplier');
          setWarehouseStock([]);
          return;
        }
      }
      
      // Calculate current stock for each item from this supplier
      const itemStockMap = new Map();
      
      console.log('Processing stocks:', stocksToProcess.length);
      stocksToProcess.forEach((stock: any, index: number) => {
        console.log(`Processing stock ${index + 1}:`, {
          id: stock.id,
          item_id: stock.item_id,
          item_name: stock.item?.name,
          quantity: stock.quantity,
          transaction_type: stock.transaction_type,
          supplier: stock.supplier?.name
        });
        
        // Ensure item_id exists - skip if missing
        const itemId = stock.item_id || stock.item?.id;
        if (!itemId) {
          console.warn(`Skipping stock ${stock.id} - no item_id found`);
          return;
        }
        if (!itemStockMap.has(itemId)) {
          itemStockMap.set(itemId, {
            item: stock.item,
            supplier: stock.supplier,
            totalIn: 0,
            totalOut: 0,
            currentStock: 0
          });
        }
        
        const itemStock = itemStockMap.get(itemId);
        
        // Ensure quantity is a number
        const quantity = parseInt(stock.quantity) || 0;
        
        if (stock.transaction_type === 'in') {
          itemStock.totalIn += quantity;
          console.log(`[Item ${itemId} - ${stock.item?.name}] Added ${quantity} to totalIn. New totalIn: ${itemStock.totalIn}`);
        } else if (stock.transaction_type === 'out') {
          itemStock.totalOut += quantity;
          console.log(`[Item ${itemId} - ${stock.item?.name}] Added ${quantity} to totalOut. New totalOut: ${itemStock.totalOut}`);
        }
        itemStock.currentStock = itemStock.totalIn - itemStock.totalOut;
        console.log(`[Item ${itemId} - ${stock.item?.name}] Current stock: ${itemStock.currentStock}`);
      });
      
      const warehouseStockData = Array.from(itemStockMap.values())
        .sort((a, b) => {
          // Sort by item name for consistent display
          const nameA = a.item?.name || '';
          const nameB = b.item?.name || '';
          return nameA.localeCompare(nameB);
        });
      
      console.log('=== FINAL WAREHOUSE STOCK RESULTS ===');
      console.log('Number of unique items:', warehouseStockData.length);
      console.log('Full Warehouse Stock Data:', JSON.stringify(warehouseStockData, null, 2));
      
      warehouseStockData.forEach((item, index) => {
        console.log(`--- Item ${index + 1} ---`);
        console.log('Name:', item.item?.name);
        console.log('Item ID:', item.item?.id);
        console.log('Total In:', item.totalIn);
        console.log('Total Out:', item.totalOut);
        console.log('Current Stock:', item.currentStock);
      });
      
      // Verify calculations match expected values
      if (warehouseStockData.length > 0) {
        console.log('\n=== CALCULATION VERIFICATION ===');
        warehouseStockData.forEach((item) => {
          const calculated = item.totalIn - item.totalOut;
          const matches = calculated === item.currentStock;
          console.log(`${item.item?.name}: ${item.totalIn} - ${item.totalOut} = ${calculated} (Displayed: ${item.currentStock}) ${matches ? '✅' : '❌'}`);
        });
      }
      
      console.log('Setting warehouse stock state...');
      setWarehouseStock(warehouseStockData);
      console.log('=== END WAREHOUSE STOCK FETCH ===');
    } catch (error: any) {
      console.error('Failed to fetch warehouse stock:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        data: error.data
      });
      
      // If API call fails, try fallback to get all stocks
      try {
        console.log('API call failed, trying fallback method...');
        const [itemsResponse, allStocksResponse] = await Promise.all([
          apiService.getItems(),
          apiService.getStocks()
        ]);
        
        const allStocks = Array.isArray(allStocksResponse.data) ? allStocksResponse.data : [];
        console.log('Fallback - All Stocks:', allStocks);
        
        // Filter by supplier ID and calculate stock
        const supplierStocks = allStocks.filter((stock: any) => 
          stock.supplier_id && stock.supplier_id.toString() === id
        );
        
        console.log('Fallback - Supplier Stocks:', supplierStocks);
        
        // Calculate current stock for each item
        const itemStockMap = new Map();
        supplierStocks.forEach((stock: any) => {
          // Ensure item_id exists - skip if missing
          const itemId = stock.item_id || stock.item?.id;
          if (!itemId) {
            console.warn(`Fallback - Skipping stock ${stock.id} - no item_id found`);
            return;
          }
          
          if (!itemStockMap.has(itemId)) {
            itemStockMap.set(itemId, {
              item: stock.item,
              supplier: stock.supplier,
              totalIn: 0,
              totalOut: 0,
              currentStock: 0
            });
          }
          
          const itemStock = itemStockMap.get(itemId);
          
          // Ensure quantity is a number
          const quantity = parseInt(stock.quantity) || 0;
          
          if (stock.transaction_type === 'in') {
            itemStock.totalIn += quantity;
          } else if (stock.transaction_type === 'out') {
            itemStock.totalOut += quantity;
          }
          itemStock.currentStock = itemStock.totalIn - itemStock.totalOut;
        });
        
        const warehouseStockData = Array.from(itemStockMap.values())
          .sort((a, b) => {
            const nameA = a.item?.name || '';
            const nameB = b.item?.name || '';
            return nameA.localeCompare(nameB);
          });
        console.log('Fallback - Final Warehouse Stock Data:', warehouseStockData);
        setWarehouseStock(warehouseStockData);
      } catch (fallbackError: any) {
        console.error('Fallback also failed:', fallbackError);
        setWarehouseStock([]);
      }
    }
  };

  const fetchSupplierOrdersList = async () => {
    if (!id) return;
    
    try {
      const response = await apiService.getOrdersBySupplier(parseInt(id));
      const orders = Array.isArray(response.data) ? response.data : [];
      
      setSupplierOrdersList(orders);
    } catch (error: any) {
      console.error('Failed to fetch supplier orders list:', error);
    }
  };

  const handleEditClick = async () => {
    if (supplier) {
      setFormData({
        ...supplier,
        supplier_type_id: supplier.supplier_type?.id?.toString() || '',
        payment_type_id: supplier.payment_type?.id?.toString() || '',
      });
      setIsEditing(true);
      setDrawerOpen(true);
      
      // Fetch dropdown data
      try {
        const [supplierTypesResponse, paymentTypesResponse] = await Promise.all([
          apiService.getSupplierTypes(),
          apiService.getPaymentTypes(),
        ]);
        setSupplierTypes(supplierTypesResponse.data || []);
        setPaymentTypes(paymentTypesResponse.data || []);
      } catch (error: any) {
        console.error('Failed to fetch dropdown data:', error);
        toast.error('Failed to load dropdown data');
      }
    }
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateSubmit = async () => {
    if (!id) return;
    
    try {
      const submitData = {
        ...formData,
        name: formData.supplier_name, // Send supplier_name as name for backend validation
        supplier_type_id: formData.supplier_type_id ? parseInt(formData.supplier_type_id) : null,
        payment_type_id: formData.payment_type_id ? parseInt(formData.payment_type_id) : null,
      };
      await apiService.updateSupplier(parseInt(id), submitData);
      toast.success('Supplier updated successfully');
      setDrawerOpen(false);
      setIsEditing(false);
      fetchSupplier();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update supplier');
    }
  };

  const handleClose = () => {
    setDrawerOpen(false);
    setIsEditing(false);
    setFormData({});
  };

  const renderFormFields = () => {
    return (
      <>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Supplier Name"
              value={formData.supplier_name || ''}
              onChange={(e) => handleFormChange('supplier_name', e.target.value)}
              margin="normal"
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              label="Contact Person"
              value={formData.contact_person || ''}
              onChange={(e) => handleFormChange('contact_person', e.target.value)}
              margin="normal"
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              label="Email Address"
              value={formData.email || ''}
              onChange={(e) => handleFormChange('email', e.target.value)}
              margin="normal"
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phone || ''}
              onChange={(e) => handleFormChange('phone', e.target.value)}
              margin="normal"
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status || 'active'}
                onChange={(e) => handleFormChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Address"
              value={formData.address || ''}
              onChange={(e) => handleFormChange('address', e.target.value)}
              margin="normal"
              multiline
              rows={3}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Supplier Type</InputLabel>
              <Select
                value={formData.supplier_type_id || ''}
                onChange={(e) => handleFormChange('supplier_type_id', e.target.value)}
                label="Supplier Type"
              >
                <MenuItem value="">None</MenuItem>
                {supplierTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id.toString()}>
                    {type.display_name || type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Payment Type</InputLabel>
              <Select
                value={formData.payment_type_id || ''}
                onChange={(e) => handleFormChange('payment_type_id', e.target.value)}
                label="Payment Type"
              >
                <MenuItem value="">None</MenuItem>
                {paymentTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id.toString()}>
                    {type.display_name || type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes || ''}
              onChange={(e) => handleFormChange('notes', e.target.value)}
              margin="normal"
              multiline
              rows={3}
            />
          </Grid>
        </Grid>
      </>
    );
  };

  const renderSupplierInquiry = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Supplier Inquiry
      </Typography>
      <Card>
        <CardContent>
          {queries.length === 0 ? (
            <Typography variant="body1" color="textSecondary">
              No queries found for this supplier.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Query ID</TableCell>
                    <TableCell>Agent</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell>Call Type</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Rating</TableCell>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queries.map((query: any) => (
                    <TableRow key={query.id}>
                      <TableCell>#{query.id}</TableCell>
                      <TableCell>{query.agent_name}</TableCell>
                      <TableCell>{query.customer_name}</TableCell>
                      <TableCell>
                        {query.supplier_name ? (
                          <Chip
                            label={query.supplier_name}
                            size="small"
                            sx={{
                              backgroundColor: '#e0f2f1',
                              color: '#0d9488',
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            No Supplier
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={query.call_type}
                          size="small"
                          sx={{
                            backgroundColor: query.call_type === 'inbound' ? '#e0f2f1' : '#fef3c7',
                            color: query.call_type === 'inbound' ? '#0d9488' : '#f59e0b',
                            textTransform: 'capitalize'
                          }}
                        />
                      </TableCell>
                      <TableCell>{query.duration}</TableCell>
                      <TableCell>
                        <Chip
                          label={query.status}
                          size="small"
                          sx={{
                            backgroundColor: query.status === 'completed' ? '#e0f2f1' : '#fee2e2',
                            color: query.status === 'completed' ? '#0d9488' : '#dc2626',
                            textTransform: 'capitalize'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {query.rating ? `⭐ ${query.rating}/5` : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {query.timestamp ? new Date(query.timestamp).toLocaleString() : 'N/A'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="body2" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {query.notes || 'No notes'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  const renderSupplierExpenses = () => {
    // Use supplierOrdersList for consistent data
    const ordersData = supplierOrdersList || [];
    
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Supplier Expenses - Orders
        </Typography>
        <Card>
          <CardContent>
            {ordersData.length === 0 ? (
              <Typography variant="body1" color="textSecondary">
                No orders found for this supplier.
              </Typography>
            ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Ticket No</TableCell>
                    <TableCell>Item Name</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Unit Price</TableCell>
                    <TableCell>Total Value</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Order Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ordersData.map((order: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell>{order.teckit_no || 'N/A'}</TableCell>
                      <TableCell>{order.item_name || 'N/A'}</TableCell>
                      <TableCell>{order.customer?.name || order.customer_name || 'N/A'}</TableCell>
                      <TableCell align="right">{order.quantity || 0}</TableCell>
                      <TableCell align="right">AED {order.unit_price || 0}</TableCell>
                      <TableCell align="right">AED {((order.quantity || 0) * (order.unit_price || 0)).toFixed(2)}</TableCell>
                      <TableCell>
                        <Chip
                          label={order.status || 'Unknown'}
                          size="small"
                          color={order.status === 'completed' ? 'success' : order.status === 'pending' ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{order.creation_date ? new Date(order.creation_date).toLocaleDateString() : 'N/A'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

  const renderSupplierSaleReport = () => {
    // Use supplierOrdersList instead of stockOutItems for consistent data
    const ordersData = supplierOrdersList || [];
    const totalOrders = ordersData.length;
    const totalQuantity = ordersData.reduce((sum, order) => sum + (order.quantity || 0), 0);
    const totalValue = ordersData.reduce((sum, order) => sum + ((order.quantity || 0) * (order.unit_price || 0)), 0);
    const averagePrice = totalQuantity > 0 ? totalValue / totalQuantity : 0;
    const averagePerOrder = totalOrders > 0 ? totalValue / totalOrders : 0;

    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Supplier Sale Report - Complete Report
        </Typography>
        
        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card sx={{ backgroundColor: '#e0f2f1' }}>
              <CardContent>
                <Typography variant="h4" sx={{ color: '#0d9488', fontWeight: 'bold' }}>
                  {totalOrders}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Orders
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card sx={{ backgroundColor: '#fef3c7' }}>
              <CardContent>
                <Typography variant="h4" sx={{ color: '#f59e0b', fontWeight: 'bold' }}>
                  {totalQuantity}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Items Sold
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card sx={{ backgroundColor: '#e0e7ff' }}>
              <CardContent>
                <Typography variant="h4" sx={{ color: '#3b82f6', fontWeight: 'bold' }}>
                  AED {totalValue.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Sales Value
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Card sx={{ backgroundColor: '#dcfce7' }}>
              <CardContent>
                <Typography variant="h4" sx={{ color: '#059669', fontWeight: 'bold' }}>
                  AED {averagePrice.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Average Price per Item
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Card sx={{ backgroundColor: '#f3e8ff' }}>
              <CardContent>
                <Typography variant="h4" sx={{ color: '#9333ea', fontWeight: 'bold' }}>
                  AED {averagePerOrder.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Average Sales per Order
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card>
          <CardContent>
            {ordersData.length === 0 ? (
              <Typography variant="body1" color="textSecondary">
                No sales data available for this supplier.
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Ticket No</TableCell>
                      <TableCell>Item Name</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Total Value</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Order Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ordersData.map((order: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>{order.teckit_no || order.order_number || 'N/A'}</TableCell>
                        <TableCell>{order.item_name || 'N/A'}</TableCell>
                        <TableCell>{order.customer?.name || order.customer_name || 'N/A'}</TableCell>
                        <TableCell align="right">{order.quantity || 0}</TableCell>
                        <TableCell align="right">AED {order.unit_price || 0}</TableCell>
                        <TableCell align="right">AED {((order.quantity || 0) * (order.unit_price || 0)).toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip
                            label={order.status || 'N/A'}
                            size="small"
                            sx={{
                              backgroundColor: order.status === 'completed' ? '#e0f2f1' : 
                                             order.status === 'pending' ? '#fef3c7' : '#fee2e2',
                              color: order.status === 'completed' ? '#0d9488' : 
                                    order.status === 'pending' ? '#f59e0b' : '#dc2626',
                              textTransform: 'capitalize'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {order.creation_date ? new Date(order.creation_date).toLocaleDateString() : 
                           order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };
  const renderReturns = () => {
    // Filter orders by cancelled/deleted status
    const cancelledOrdersData = (supplierOrdersList || []).filter((order: any) => 
      order.status === 'cancelled' || order.status === 'deleted'
    );
    
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Returns - Cancelled/Deleted Orders
        </Typography>
        <Card>
          <CardContent>
            {cancelledOrdersData.length === 0 ? (
              <Typography variant="body1" color="textSecondary">
                No cancelled or deleted orders found for this supplier.
              </Typography>
            ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Order ID</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Items</TableCell>
                    <TableCell>Total Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Order Date</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cancelledOrdersData.map((order: any) => (
                    <TableRow key={order.id}>
                      <TableCell>{order.teckit_no || 'N/A'}</TableCell>
                      <TableCell>{order.customer?.name || order.customer_name || 'N/A'}</TableCell>
                      <TableCell>{order.item_name || 'N/A'}</TableCell>
                      <TableCell align="right">AED {((order.quantity || 0) * (order.unit_price || 0)).toFixed(2)}</TableCell>
                      <TableCell>
                        <Chip
                          label={order.status || 'N/A'}
                          size="small"
                          color="error"
                        />
                      </TableCell>
                      <TableCell>
                        {order.creation_date ? new Date(order.creation_date).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="body2" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {order.notes || 'No notes'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

  const renderProcess = () => {
    // Filter orders by pending status
    const pendingOrdersData = (supplierOrdersList || []).filter((order: any) => 
      order.status === 'pending'
    );
    
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Process - Pending Orders
        </Typography>
        <Card>
          <CardContent>
            {pendingOrdersData.length === 0 ? (
              <Typography variant="body1" color="textSecondary">
                No pending orders found for this supplier.
              </Typography>
            ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Order ID</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Items</TableCell>
                    <TableCell>Total Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Order Date</TableCell>
                    <TableCell>Expected Delivery</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingOrdersData.map((order: any) => (
                    <TableRow key={order.id}>
                      <TableCell>{order.teckit_no || 'N/A'}</TableCell>
                      <TableCell>{order.customer?.name || order.customer_name || 'N/A'}</TableCell>
                      <TableCell>{order.item_name || 'N/A'}</TableCell>
                      <TableCell align="right">AED {((order.quantity || 0) * (order.unit_price || 0)).toFixed(2)}</TableCell>
                      <TableCell>
                        <Chip
                          label={order.status || 'N/A'}
                          size="small"
                          color="warning"
                        />
                      </TableCell>
                      <TableCell>
                        {order.creation_date ? new Date(order.creation_date).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {order.effective_end_date ? new Date(order.effective_end_date).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="body2" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {order.notes || 'No notes'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
  };

  const renderReports = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Reports
      </Typography>
      <Card>
        <CardContent>
          <Typography variant="body1">
            This section contains reports information and related data.
          </Typography>
          {/* Add specific reports content here */}
        </CardContent>
      </Card>
    </Box>
  );

  const renderOrdersListing = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Orders Listing - Items Sold
      </Typography>
      <Card>
        <CardContent>
          {supplierOrdersList.length === 0 ? (
            <Typography variant="body1" color="textSecondary">
              No orders found for this supplier.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Ticket No</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Item Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Unit Price</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Total Value</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Order Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {supplierOrdersList.map((order: any, index: number) => (
                    <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                      <TableCell sx={{ color: '#1f2937', fontWeight: 500 }}>
                        {order.teckit_no || order.order_number || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ color: '#1f2937' }}>
                        {order.item_name || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ color: '#1f2937' }}>
                        {order.customer?.name || order.customer_name || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ color: '#1f2937', textAlign: 'center' }}>
                        {order.quantity || 0}
                      </TableCell>
                      <TableCell sx={{ color: '#1f2937', textAlign: 'right' }}>
                        AED {order.unit_price || 0}
                      </TableCell>
                      <TableCell sx={{ color: '#1f2937', textAlign: 'right', fontWeight: 600 }}>
                        AED {((order.quantity || 0) * (order.unit_price || 0)).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.status || 'N/A'}
                          size="small"
                          sx={{
                            backgroundColor: order.status === 'completed' ? '#e0f2f1' : 
                                           order.status === 'pending' ? '#fef3c7' : 
                                           order.status === 'cancelled' ? '#fee2e2' : '#f3f4f6',
                            color: order.status === 'completed' ? '#0d9488' : 
                                  order.status === 'pending' ? '#f59e0b' : 
                                  order.status === 'cancelled' ? '#dc2626' : '#6b7280',
                            textTransform: 'capitalize',
                            fontWeight: 500
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#6b7280' }}>
                        {order.creation_date ? new Date(order.creation_date).toLocaleDateString() : 
                         order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell sx={{ color: '#6b7280', maxWidth: 200 }}>
                        <Typography variant="body2" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {order.notes || 'No notes'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  const renderWarehouseStock = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Warehouse Stock
      </Typography>
      <Card>
        <CardContent>
          {warehouseStock.length === 0 ? (
            <Typography variant="body1" color="textSecondary">
              No items found for this supplier.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Item Code</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Item Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Unit</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Total In</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Total Out</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Current Stock</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {warehouseStock.map((itemStock: any, index: number) => (
                    <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                      <TableCell sx={{ color: '#1f2937' }}>
                        {itemStock.item?.item_code || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ color: '#1f2937' }}>
                        {itemStock.item?.name || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ color: '#6b7280' }}>
                        {itemStock.item?.unit || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ color: '#1f2937' }}>
                        {itemStock.totalIn}
                      </TableCell>
                      <TableCell sx={{ color: '#1f2937' }}>
                        {itemStock.totalOut}
                      </TableCell>
                      <TableCell sx={{ color: '#1f2937' }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: itemStock.currentStock > 0 ? '#10b981' : '#ef4444'
                          }}
                        >
                          {itemStock.currentStock}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={itemStock.currentStock > 0 ? 'In Stock' : 'Out of Stock'}
                          size="small"
                          sx={{
                            backgroundColor: itemStock.currentStock > 0 ? '#e0f2f1' : '#fee2e2',
                            color: itemStock.currentStock > 0 ? '#0d9488' : '#dc2626',
                            textTransform: 'capitalize'
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  const renderCurrentView = () => {
    switch (currentView) {
      case 'warehouse_stock':
        return renderWarehouseStock();
      case 'orders_listing':
        return renderOrdersListing();
      case 'supplier_inquiry':
        return renderSupplierInquiry();
      case 'supplier_expenses':
        return renderSupplierExpenses();
      case 'supplier_sale_report':
        return renderSupplierSaleReport();
      case 'returns':
        return renderReturns();
      case 'process':
        return renderProcess();
      case 'reports':
        return renderReports();
      default:
        return renderWarehouseStock();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Loading supplier details...</Typography>
      </Box>
    );
  }

  if (!supplier) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Supplier not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => navigate('/suppliers')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1e40af' }}>
            Supplier Details
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left Sidebar - Supplier Submenus */}
        <Grid size={{ xs: 12, md: 3 }}>
          <SettingsSidebar 
            variant="supplier_details" 
            currentView={currentView}
            onViewChange={setCurrentView}
          />
        </Grid>

        {/* Right Content Area */}
        <Grid size={{ xs: 12, md: 9 }}>
          {/* Supplier Info Card */}
          <Card sx={{ mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: '16px' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ width: 60, height: 60, backgroundColor: '#3b82f6', mr: 2 }}>
                    <Business sx={{ fontSize: 30 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e40af' }}>
                      {supplier?.name || supplier?.supplier_name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      {supplier?.contact_person || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<Edit />}
                    onClick={handleEditClick}
                    sx={{
                      backgroundColor: '#e0f2f1',
                      color: '#0d9488',
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: '#ccedea',
                      },
                    }}
                  >
                    Edit Supplier
                  </Button>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 3 }}>
                {/* Left Column - Labels */}
                <Box sx={{ flex: '0 0 140px' }}>
                  <Typography variant="body2" sx={{ color: '#9ca3af', mb: 2.5, fontSize: '0.813rem' }}>
                    Email Address
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#9ca3af', mb: 2.5, fontSize: '0.813rem' }}>
                    Phone Number
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#9ca3af', mb: 2.5, fontSize: '0.813rem' }}>
                    Current Stock
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#9ca3af', mb: 2.5, fontSize: '0.813rem' }}>
                    Status
                  </Typography>
                  {supplier?.address && (
                    <Typography variant="body2" sx={{ color: '#9ca3af', mb: 2.5, fontSize: '0.813rem' }}>
                      Address
                    </Typography>
                  )}
                  {supplier?.notes && (
                    <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: '0.813rem' }}>
                      Notes
                    </Typography>
                  )}
                </Box>

                {/* Right Column - Values */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: '#111827', mb: 2.5, fontSize: '0.875rem', fontWeight: 500 }}>
                    {supplier?.email || 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#111827', mb: 2.5, fontSize: '0.875rem', fontWeight: 500 }}>
                    {supplier?.phone || 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#111827', mb: 2.5, fontSize: '0.875rem', fontWeight: 600 }}>
                    {supplier?.current_stock || 0}
                  </Typography>
                  <Box sx={{ mb: 2.5 }}>
                    <Chip
                      label={supplier?.status}
                      size="small"
                      sx={{
                        backgroundColor: supplier?.status === 'active' ? '#dcfce7' : '#fee2e2',
                        color: supplier?.status === 'active' ? '#059669' : '#dc2626',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: '24px',
                      }}
                    />
                  </Box>
                  {supplier?.address && (
                    <Typography variant="body2" sx={{ color: '#111827', mb: 2.5, fontSize: '0.875rem', fontWeight: 500 }}>
                      {supplier?.address}
                    </Typography>
                  )}
                  {supplier?.notes && (
                    <Typography variant="body2" sx={{ color: '#111827', fontSize: '0.875rem', fontWeight: 500 }}>
                      {supplier?.notes}
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Dynamic Content Based on Current View */}
          {renderCurrentView()}
        </Grid>
      </Grid>

      {/* Edit Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: 600,
            padding: 3,
          },
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e40af' }}>
            {isEditing ? 'Edit Supplier' : 'Add Supplier'}
          </Typography>
        </Box>
        
        {renderFormFields()}
        
        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              flex: 1,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateSubmit}
            sx={{
              flex: 1,
              backgroundColor: '#3b82f6',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#2563eb',
              },
            }}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </Box>
      </Drawer>

    </Box>
  );
}

export default SupplierDetails;